/**
 * TypeBox schemas for TripFeature, FeatureStyle, and FeatureLabel entities
 */

import { Type, type Static } from 'typebox'
import { UUID, ISODate, HexColor, Bounds, Coordinates, type Exact } from './common.js'

// --- FeatureStyle ---

export const FeatureStyleSchema = Type.Object({
  pointColor: Type.Optional(HexColor),
  pointIcon: Type.Optional(Type.String()),
  pointSize: Type.Optional(Type.Number({ minimum: 1, maximum: 100 })),
  pointOpacity: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
  markerType: Type.Optional(
    Type.Union([
      Type.Literal('none'),
      Type.Literal('circle'),
      Type.Literal('square'),
      Type.Literal('triangle'),
      Type.Literal('pointer'),
    ])
  ),
  markerIcon: Type.Optional(Type.String()),
  markerSize: Type.Optional(Type.Number()),
  markerPosition: Type.Optional(
    Type.Union([Type.Literal('center'), Type.Literal('above'), Type.Literal('below')])
  ),
  lineColor: Type.Optional(HexColor),
  lineWidth: Type.Optional(Type.Number({ minimum: 1, maximum: 20 })),
  lineOpacity: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
  lineDashArray: Type.Optional(Type.Array(Type.Number())),
  linePattern: Type.Optional(
    Type.Union([
      Type.Literal('solid'),
      Type.Literal('dashed'),
      Type.Literal('dotted'),
      Type.Literal('dash-dot'),
      Type.Literal('long-dash'),
    ])
  ),
  fillColor: Type.Optional(HexColor),
  fillOpacity: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
  fillPattern: Type.Optional(
    Type.Union([
      Type.Literal('solid'),
      Type.Literal('hatch'),
      Type.Literal('cross-hatch'),
      Type.Literal('dots'),
      Type.Literal('diagonal'),
      Type.Literal('none'),
    ])
  ),
  strokeColor: Type.Optional(HexColor),
  strokeWidth: Type.Optional(Type.Number()),
  strokeOpacity: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
  strokeDashArray: Type.Optional(Type.Array(Type.Number())),
  strokePattern: Type.Optional(
    Type.Union([
      Type.Literal('solid'),
      Type.Literal('dashed'),
      Type.Literal('dotted'),
      Type.Literal('dash-dot'),
      Type.Literal('long-dash'),
    ])
  ),
  radius: Type.Optional(Type.Number()),
  cornerRadius: Type.Optional(Type.Number()),
})
export type FeatureStyle = Exact<Static<typeof FeatureStyleSchema>>

// --- FeatureLabel ---

export const LabelPositionSchema = Type.Union([
  Type.Literal('center'),
  Type.Literal('top'),
  Type.Literal('bottom'),
  Type.Literal('left'),
  Type.Literal('right'),
  Type.Literal('custom'),
])

export const FeatureLabelSchema = Type.Object({
  text: Type.String({ minLength: 1, maxLength: 100 }),
  position: LabelPositionSchema,
  customPosition: Type.Optional(Coordinates),
  fontSize: Type.Optional(Type.Number({ minimum: 8, maximum: 72 })),
  fontColor: Type.Optional(Type.String()),
  backgroundColor: Type.Optional(Type.String()),
  padding: Type.Optional(Type.Number()),
  visible: Type.Boolean(),
  fontFamily: Type.Optional(Type.String()),
  verticalAlign: Type.Optional(
    Type.Union([Type.Literal('top'), Type.Literal('middle'), Type.Literal('bottom')])
  ),
  horizontalAlign: Type.Optional(
    Type.Union([Type.Literal('left'), Type.Literal('center'), Type.Literal('right')])
  ),
})
export type FeatureLabel = Exact<Static<typeof FeatureLabelSchema>>

// --- TripFeature ---

export const FeatureTypeSchema = Type.Union([
  Type.Literal('point'),
  Type.Literal('line'),
  Type.Literal('polygon'),
  Type.Literal('circle'),
  Type.Literal('rectangle'),
])

/** GeoJSON geometry types we support */
export const GeoJSONGeometryTypeSchema = Type.Union([
  Type.Literal('Point'),
  Type.Literal('LineString'),
  Type.Literal('Polygon'),
])

/** GeoJSON.Geometry preserved for TypeScript; runtime validation is structural */
const GeoJSONGeometry = Type.Unsafe<GeoJSON.Geometry>(
  Type.Object({
    type: GeoJSONGeometryTypeSchema,
    coordinates: Type.Unknown(),
  })
)

export const TripFeatureSchema = Type.Object({
  id: UUID,
  name: Type.String({ minLength: 1, maxLength: 100 }),
  description: Type.Optional(Type.String({ maxLength: 1000 })),
  type: FeatureTypeSchema,
  geometry: GeoJSONGeometry,
  properties: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
  style: Type.Optional(FeatureStyleSchema),
  label: Type.Optional(FeatureLabelSchema),
  layerId: Type.Optional(UUID),
  tripIds: Type.Array(UUID),
  createdAt: ISODate,
  updatedAt: ISODate,
  createdBy: UUID,
  isVisible: Type.Boolean(),
  bounds: Type.Optional(Bounds),
  isLegacyBounds: Type.Optional(Type.Boolean()),
})
export type TripFeature = Exact<Static<typeof TripFeatureSchema>>

/** Fields required when creating a trip feature */
export const TripFeatureCreateSchema = Type.Object({
  name: Type.String({ minLength: 1, maxLength: 100 }),
  type: FeatureTypeSchema,
  geometry: GeoJSONGeometry,
  tripIds: Type.Array(UUID),
  createdBy: UUID,
  isVisible: Type.Boolean(),
})

/** Partial trip feature for updates */
export const TripFeatureUpdateSchema = Type.Partial(TripFeatureSchema)
